import {FC, Fragment, useState, useEffect, useCallback} from "react"

import {isPlainObject} from "@agenta/shared/utils"

import styles from "./FormView.module.css"
import {CustomRenderFn} from "./nodes/NodeTypes"
import renderNode from "./nodes/renderNode"
export interface FormViewProps {
    value: Record<string, unknown>
    onChange: (v: Record<string, unknown>) => void
    customRender?: CustomRenderFn
}

const prepareInitialValues = (obj: unknown): unknown => {
    if (Array.isArray(obj)) {
        return obj.map((item) => prepareInitialValues(item))
    }
    if (isPlainObject(obj)) {
        const out: Record<string, unknown> = {}
        Object.entries(obj).forEach(([k, v]) => {
            if (isPlainObject(v)) {
                out[k] = prepareInitialValues(v) // keep objects nested
            } else if (Array.isArray(v)) {
                out[k] = v.map((item) => prepareInitialValues(item))
            } else {
                out[k] = v
            }
        })
        return out
    }
    return obj
}

// Deep-clone helper using structuredClone when available
const deepClone = <T,>(obj: T): T =>
    typeof structuredClone === "function" ? structuredClone(obj) : JSON.parse(JSON.stringify(obj))

// Rename a key in a nested object based on path
const renameKey = (
    root: Record<string, unknown>,
    path: (string | number)[],
    newKey: string,
): Record<string, unknown> => {
    if (path.length === 0) return root
    const cloned = deepClone(root)
    let cursor: Record<string, unknown> = cloned
    for (let i = 0; i < path.length - 1; i++) {
        const seg = path[i]
        cursor = cursor[seg] as Record<string, unknown>
        if (cursor === undefined) return cloned // path invalid
    }
    const last = path[path.length - 1] as string
    if (typeof last !== "string") return cloned // arrays not renamable
    const reordered: Record<string, unknown> = {}
    Object.entries(cursor).forEach(([key, val]) => {
        if (key === last) {
            reordered[newKey] = val
        } else {
            reordered[key] = val
        }
    })
    // replace object with reordered keys
    Object.keys(cursor).forEach((k) => delete cursor[k])
    Object.assign(cursor, reordered)
    return cloned
}

// handleRename will be defined inside component to access form instance

const parseMaybeJsonDeep = (val: unknown): unknown => {
    if (typeof val === "string") {
        const trimmed = val.trim()
        if (
            (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
            (trimmed.startsWith("[") && trimmed.endsWith("]"))
        ) {
            try {
                return JSON.parse(trimmed)
            } catch {
                return val
            }
        }
        return val
    }
    if (Array.isArray(val)) return val.map(parseMaybeJsonDeep)
    if (isPlainObject(val)) {
        const result: Record<string, unknown> = {}
        Object.entries(val).forEach(([k, v]) => {
            result[k] = parseMaybeJsonDeep(v)
        })
        return result
    }
    return val
}

const FormView: FC<FormViewProps> = ({value, onChange, customRender}) => {
    const [formValuesRef, setFormValuesRef] = useState<Record<string, unknown>>({})
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
    const toggleFold = useCallback((key: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }, [])

    useEffect(() => {
        const newValues = prepareInitialValues(value) as Record<string, unknown>
        setFormValuesRef(newValues)
    }, [value])

    const handleRename = useCallback(
        (path: (string | number)[], newKey: string) => {
            const newValues = renameKey(formValuesRef, path, newKey)
            onChange(newValues)
        },
        [formValuesRef, onChange],
    )

    const boundHandleValuesChange = useCallback(
        (path: (string | number)[], newValue: unknown) => {
            const updatedRoot = structuredClone(formValuesRef)

            // walk to parent
            const parent = path
                .slice(0, -1)
                .reduce<unknown>((acc, key) => (acc as Record<string, unknown>)[key], updatedRoot)
            const lastKey = path[path.length - 1]
            if (parent !== undefined) {
                ;(parent as Record<string | number, unknown>)[lastKey] = newValue
                // antd's onValuesChange used to normalise JSON-ish strings here; keep that.
                onChange(parseMaybeJsonDeep(updatedRoot) as Record<string, unknown>)
            }
        },
        [formValuesRef, onChange],
    )

    const boundHandleRename = useCallback(
        (path: (string | number)[], newKey: string) => {
            handleRename(path, newKey)
        },
        [handleRename],
    )

    return formValuesRef ? (
        <div className={styles["form-view"]}>
            <div>
                {Object.entries(formValuesRef).map(([k, v]) => (
                    <Fragment key={k}>
                        {renderNode({
                            path: [k],
                            k,
                            value: v,
                            depth: 0,
                            collapsed,
                            toggleFold,
                            onChange: boundHandleValuesChange,
                            customRender,
                            handleRename: boundHandleRename,
                        })}
                    </Fragment>
                ))}
            </div>
        </div>
    ) : null
}

export default FormView
